import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { richTextToBlocks } from '../../utils/richTextToBlocks';
import {
  stripLeadingDuplicateHeadingBlocks,
  isM11AppendixSectionTitle,
  outlineLevelFromSectionTitle,
} from '../../utils/protocolExportContent';
import { prepareSectionContentForExport, computeMediaNumberOffsets } from '../../utils/formattingUtils';
import { EMPTY_PROTOCOL_HEADER_FOOTER } from '../../utils/protocolExportHeaderFooter';
import { PDF_FONT_BOLD, PDF_FONT_REGULAR, pdfFontFamily, sanitizeLineHeight, sanitizePdfPt } from '../../utils/pdfFontSetup';
import { renderHybridPdfText } from '../../utils/renderHybridPdfText';
import { createTableCaptionTracker, extractPdfTableDescription } from '../../utils/pdfTableCaption';
import {
  PDF_LANDSCAPE_MARGIN_H_PT,
  PDF_PAGE_MARGIN_BOTTOM_PT,
  PDF_PAGE_MARGIN_TOP_PT,
  PDF_PORTRAIT_MARGIN_H_PT,
  PDF_PORTRAIT_USABLE_WIDTH_PT,
} from '../../utils/protocolPdfLayout';
import ProtocolPdfHeaderFooter from './ProtocolPdfHeaderFooter';
import ProtocolPdfTitlePage from './ProtocolPdfTitlePage';
import { PdfTable, renderTextRuns } from './pdfTable';
import SoaPdfTable from './SoaPdfTable';
import { normalizeScheduleOfActivitiesHeading } from '../../utils/soaTableExportModel';

const PORTRAIT_MARGIN_H = PDF_PORTRAIT_MARGIN_H_PT;
const LANDSCAPE_MARGIN_H = PDF_LANDSCAPE_MARGIN_H_PT;
const PAGE_MARGIN_TOP = PDF_PAGE_MARGIN_TOP_PT;
const PAGE_MARGIN_BOTTOM = PDF_PAGE_MARGIN_BOTTOM_PT;

const DEFAULT_FONT = {
  heading: { size: 14 },
  subheading: { size: 12 },
  heading3: { size: 11 },
  body: { size: 12, lineSpacing: 1.15 },
  list: { size: 11, lineSpacing: 1.15 },
  tableHeader: { size: 11, bold: true },
  tableCell: { size: 11, bold: false },
};

function isSoaSection(title) {
  const t = String(title || '');
  return /SCHEDULE OF ACTIVITIES|\bSOA\b|^1\.3\b/i.test(t);
}

function isSchemaSection(title) {
  return /\bTRIAL SCHEMA\b/i.test(title || '');
}

/** True when structured SOA tables exist for native PDF (legacy and/or chat S9). */
function hasUsableSoaData(data) {
  if (!data || typeof data !== 'object') return false;
  if (Array.isArray(data?.s9?.tables) && data.s9.tables.length > 0) return true;
  if (Array.isArray(data?.tables) && data.tables.length > 0) return true;
  const hasVisits = Array.isArray(data.visits) && data.visits.length > 0;
  const hasSections = Array.isArray(data.sections) && data.sections.length > 0;
  return hasVisits && hasSections;
}

/** Main section number only, e.g. "9.1 Title" → "9". */
function mainSectionNumber(title) {
  const m = String(title || '').trim().match(/^(\d+)\b/);
  return m ? m[1] : '';
}

function sectionsToPageSpecs(sections, soaData, trialSchemaImageUrl) {
  const specs = [];
  let includedSoa = false;

  for (const s of sections) {
    if (!s?.title) continue;
    const hasOutput = s.output && String(s.output).trim();
    const isSoa = isSoaSection(s.title);
    const isSchema = isSchemaSection(s.title);

    // SOA / Trial Schema content often lives outside section.output (structured SOA / image).
    // Always include them when that external content exists.
    if (!hasOutput && !isM11AppendixSectionTitle(s.title)) {
      if (isSoa && hasUsableSoaData(soaData)) {
        specs.push({
          section: { ...s, output: s.output || '<p></p>' },
          orientation: 'landscape',
        });
        includedSoa = true;
      } else if (isSchema && trialSchemaImageUrl) {
        specs.push({
          section: { ...s, output: s.output || '<p></p>' },
          orientation: 'portrait',
        });
      }
      continue;
    }

    specs.push({
      section: hasOutput ? s : { ...s, output: '<p>Not applicable.</p>' },
      orientation: isSoa && hasUsableSoaData(soaData) ? 'landscape' : 'portrait',
    });
    if (isSoa && hasUsableSoaData(soaData)) includedSoa = true;
  }

  // Published SOA exists but protocol list has no matching 1.3 section — still emit landscape SOA
  if (hasUsableSoaData(soaData) && !includedSoa) {
    specs.push({
      section: {
        title: '1.3 SCHEDULE OF ACTIVITIES',
        output: '<p></p>',
        _id: 'synthetic-soa-1.3',
      },
      orientation: 'landscape',
    });
  }

  // Continue Table/Figure numbering across sections sharing a main number, matching the
  // Word export and the editor (see computeMediaNumberOffsets in formattingUtils.js).
  const offsets = computeMediaNumberOffsets(specs.map((spec) => spec.section));
  return specs.map((spec, i) => ({ ...spec, section: { ...spec.section, mediaNumbering: offsets[i] } }));
}

/**
 * Ordered {level (1-4), text} heading list for the native PDF Table of Contents —
 * mirrors exactly what renderSectionBlock/renderBlocks put on the page (same
 * sectionsToPageSpecs + richTextToBlocks pipeline), so TOC entries never drift from
 * what's actually rendered. Page numbers aren't known yet; a later pass locates each
 * entry's page in the rendered PDF (see nativeProtocolPdfToc.js).
 */
export function buildProtocolPdfTocEntries(sections, soaData, trialSchemaImageUrl) {
  const pageSpecs = sectionsToPageSpecs(sections, soaData, trialSchemaImageUrl);
  const entries = [];

  pageSpecs.forEach((spec) => {
    const s = spec.section;
    if (!s?.title) return;

    const isSoa = isSoaSection(s.title) && hasUsableSoaData(soaData);
    const isSchema = isSchemaSection(s.title) && trialSchemaImageUrl;
    const displayTitle = isSoa ? normalizeScheduleOfActivitiesHeading(s.title) : s.title;
    const sectionLevel = Math.min(outlineLevelFromSectionTitle(s.title) + 1, 4);
    entries.push({ level: sectionLevel, text: displayTitle });

    // Schema/SOA sections render an image/table, not textual content with embedded headings.
    if (isSoa || isSchema) return;

    const prepared = stripNonPdfHtml(prepareSectionContentForExport(s.output, s.title, s.mediaNumbering));
    const blocks = trimLeadingEmptyBlocks(
      stripLeadingDuplicateHeadingBlocks(
        richTextToBlocks(prepared, {
          sectionRootPrefix: (s.title.match(/^(\d+(?:\.\d+)*)/) || [])[1] || null,
        }),
        s.title,
      ),
    );

    blocks.forEach((b) => {
      if (b.type === 'heading') {
        entries.push({ level: Math.min(Math.max(b.level || 2, 1), 4), text: b.text });
      }
    });
  });

  return entries;
}

function buildPdfStyles(fontConfig) {
  const fc = fontConfig || DEFAULT_FONT;
  const bodySize = sanitizePdfPt(fc.body?.size, 12);
  const bodyLineSpacing = sanitizeLineHeight(fc.body?.lineSpacing ?? 1.15);
  const listSize = sanitizePdfPt(fc.list?.size, 11);
  const listLineSpacing = sanitizeLineHeight(fc.list?.lineSpacing ?? 1.15);

  return StyleSheet.create({
    // Do NOT set lineHeight on Page — react-pdf re-resolves it on every
    // pagination pass with fixed footers and eventually crashes with
    // "unsupported number: -1.37e+22" (see diegomura/react-pdf#3452).
    page: {
      paddingTop: PAGE_MARGIN_TOP,
      paddingBottom: PAGE_MARGIN_BOTTOM,
      paddingHorizontal: PORTRAIT_MARGIN_H,
      fontSize: bodySize,
      fontFamily: PDF_FONT_REGULAR,
    },
    landscapePage: {
      paddingTop: PAGE_MARGIN_TOP,
      paddingBottom: PAGE_MARGIN_BOTTOM,
      paddingHorizontal: LANDSCAPE_MARGIN_H,
      fontSize: sanitizePdfPt(fc.tableCell?.size, 11),
      fontFamily: PDF_FONT_REGULAR,
    },
    sectionBlock: {
      marginBottom: 4,
    },
    sectionTitle: {
      fontSize: sanitizePdfPt(fc.heading?.size, 14),
      marginBottom: 4,
      marginTop: 0,
      fontFamily: PDF_FONT_BOLD,
    },
    h1: {
      fontSize: sanitizePdfPt(fc.heading?.size, 14),
      marginBottom: 4,
      marginTop: 0,
      fontFamily: PDF_FONT_BOLD,
    },
    h2: {
      fontSize: sanitizePdfPt(fc.subheading?.size, 12),
      marginBottom: 3,
      marginTop: 0,
      fontFamily: PDF_FONT_BOLD,
    },
    h3: {
      fontSize: sanitizePdfPt(fc.heading3?.size, 11),
      marginBottom: 3,
      marginTop: 0,
      fontFamily: PDF_FONT_BOLD,
    },
    h4: {
      fontSize: sanitizePdfPt(fc.heading4?.size ?? fc.heading3?.size, 11),
      marginBottom: 3,
      marginTop: 0,
      fontFamily: PDF_FONT_BOLD,
    },
    p: {
      fontSize: bodySize,
      marginBottom: 3,
      marginTop: 0,
      textAlign: 'left',
      lineHeight: bodyLineSpacing,
    },
    listItemText: {
      fontSize: listSize,
      marginLeft: 14,
      marginBottom: 2,
      marginTop: 0,
      textAlign: 'left',
      lineHeight: listLineSpacing,
    },
  });
}

function headingStyleForBlockLevel(level, styles) {
  if (level <= 1) return styles.h1;
  if (level === 2) return styles.h2;
  if (level === 3) return styles.h3;
  return styles.h4;
}

function firstTableHeaderPlain(rows) {
  const cell = rows?.[0]?.[0];
  if (!cell) return '';
  if (Array.isArray(cell)) return cell.map((r) => r.text || '').join('').trim();
  return String(cell).trim();
}

function trimLeadingEmptyBlocks(blocks) {
  if (!Array.isArray(blocks)) return [];
  let start = 0;
  while (start < blocks.length) {
    const block = blocks[start];
    if (block.type === 'paragraph') {
      const text = (block.runs || []).map((run) => run.text || '').join('').trim();
      if (!text) {
        start += 1;
        continue;
      }
    }
    break;
  }
  return start > 0 ? blocks.slice(start) : blocks;
}

function flattenHybridTextNodes(nodes) {
  if (nodes == null) return [];
  if (typeof nodes === 'string') return [nodes];
  if (Array.isArray(nodes)) return nodes.flatMap((node) => flattenHybridTextNodes(node));
  return [nodes];
}

function renderBlocks(blocks, styles, fontConfig, captionTracker, sectionTitle, titleElement) {
  // If there's a title element to place and the section's very first content block is a
  // table, glue the title into that table's atomic first-chunk block (see PdfTable's
  // sectionTitle prop) instead of rendering it as an ordinary leading sibling — otherwise a
  // forced page break before the table's first chunk can strand the bare heading at the
  // bottom of the previous page with nothing else under it. Any other block type already has
  // real content immediately following the title on the same page-flow, so no gluing is
  // needed there.
  const titleGoesIntoFirstTable = Boolean(titleElement) && blocks[0]?.type === 'table';

  // Imperative (not .map()) so the 'table' case below can reach back and null out an
  // already-rendered sibling — needed to un-render an embedded caption paragraph once it's
  // decided to glue that paragraph into the table instead (see the 'table' case).
  const rendered = new Array(blocks.length).fill(null);

  blocks.forEach((b, i) => {
    switch (b.type) {
      case 'heading': {
        const style = headingStyleForBlockLevel(b.level ?? 2, styles);
        rendered[i] = (
          <Text key={i} style={style}>
            {flattenHybridTextNodes(renderHybridPdfText(b.text, style, `h-${i}-`))}
          </Text>
        );
        break;
      }
      case 'paragraph': {
        const runs = b.runs || [{ text: '' }];
        const hasFormatting = runs.some((r) => r.bold || r.italic);
        if (!hasFormatting) {
          const children = runs.flatMap((r, idx) =>
            flattenHybridTextNodes(renderHybridPdfText(r.text, styles.p, `p-${i}-${idx}-`)),
          );
          rendered[i] = (
            <Text key={i} style={styles.p}>
              {children}
            </Text>
          );
          break;
        }
        const text = runs.map((r, idx) => {
          const runStyle = {
            fontFamily: pdfFontFamily({ bold: r.bold, italic: r.italic }),
            fontSize: styles.p.fontSize,
          };
          return (
            <Text key={idx} style={runStyle}>
              {flattenHybridTextNodes(renderHybridPdfText(r.text, runStyle, `p-${i}-${idx}-`))}
            </Text>
          );
        });
        rendered[i] = (
          <Text key={i} style={styles.p}>
            {text}
          </Text>
        );
        break;
      }
      case 'list': {
        rendered[i] = (
          <View key={i} style={{ marginBottom: 4 }}>
            {b.items.map((runs, j) => {
              const itemText = runs.map((r) => r.text).join('');
              const prefix = b.ordered ? `${j + 1}. ` : '• ';
              return (
                <Text key={j} style={styles.listItemText}>
                  {flattenHybridTextNodes(
                    renderHybridPdfText(`${prefix}${itemText}`, styles.listItemText, `li-${i}-${j}-`),
                  )}
                </Text>
              );
            })}
          </View>
        );
        break;
      }
      case 'table': {
        const description = extractPdfTableDescription(
          sectionTitle,
          1,
          firstTableHeaderPlain(b.rows),
        );
        // Content generation bakes TWO consecutive paragraphs immediately before
        // every table: a "Table N-M summarizes/presents/lists..." cross-reference
        // sentence, then a separate bold "Table N-M: Title" caption paragraph. Both
        // start with "Table <number>", so walk backward through the whole run of
        // matching paragraphs (not just the single immediate predecessor) — rendering
        // another caption here would duplicate whichever of them already reads as one,
        // and this one's title is section-derived, not table-specific, so it's often
        // wrong too. Still call the tracker so later un-captioned tables in this
        // section keep the correct ordinal.
        const isTableLeadIn = (block) =>
          block?.type === 'paragraph' &&
          /^Table\s+\d+/i.test((block.runs || []).map((r) => r.text || '').join('').trim());

        let leadInStart = i;
        while (leadInStart > 0 && isTableLeadIn(blocks[leadInStart - 1])) {
          leadInStart -= 1;
        }
        const hasEmbeddedCaption = leadInStart < i;
        const trackedCaption = captionTracker.next(sectionTitle, description);
        const caption = hasEmbeddedCaption ? null : trackedCaption;

        // That run of embedded lead-in paragraphs is the last thing before this table's
        // atomic, forced-fresh-page first chunk — the same orphan risk solved for the
        // section title (titleGoesIntoFirstTable, above): plain flowing paragraphs have
        // no space guarantee, so they can be left stranded at the bottom of a page while
        // the whole table relocates to the next one. Glue the whole run into the chunk
        // instead of leaving any of it as an ordinary paragraph: drop each from its own
        // slot (already rendered earlier in this loop) and hand them, in original order,
        // to PdfTable's sectionTitle prop, which places them inside chunk 0's atomic
        // block ahead of the real caption/table content.
        let glueElement = null;
        if (i === 0 && titleGoesIntoFirstTable) {
          glueElement = titleElement;
        } else if (hasEmbeddedCaption) {
          const leadInElements = [];
          for (let j = leadInStart; j < i; j += 1) {
            if (rendered[j]) leadInElements.push(rendered[j]);
            rendered[j] = null;
          }
          glueElement = leadInElements.length ? <>{leadInElements}</> : null;
        }

        rendered[i] = (
          <PdfTable
            key={i}
            rows={b.rows}
            fontConfig={fontConfig}
            renderCell={renderTextRuns}
            caption={caption}
            sectionTitle={glueElement}
          />
        );
        break;
      }
      default:
        rendered[i] = null;
    }
  });

  const filtered = rendered.filter(Boolean);
  return titleElement && !titleGoesIntoFirstTable ? [titleElement, ...filtered] : filtered;
}

// <pre class="mermaid"> / <pre class="liver-event-mermaid"> (see formatLiverSectionHtml in
// patientLibrarySection7111.js) is a hydration point for DocumentCanvas's browser-side
// Mermaid.js rendering (ProtocolHtmlContent queries pre.mermaid, pre.liver-event-mermaid and
// replaces each with a rendered SVG). Native PDF export has no DOM and never runs that
// hydration pass, so richTextToBlocks fell through to its generic tag handling for <pre>,
// which isn't in its recognized tag list — the element's escaped text-node child (the raw
// Mermaid diagram source, e.g. "flowchart TD startNode[...] --> ...") was extracted as an
// ordinary paragraph and rendered as unreadable diagram-definition text in the PDF.
const MERMAID_PRE_BLOCK = /<pre\b[^>]*class="[^"]*\bmermaid\b[^"]*"[^>]*>[\s\S]*?<\/pre>/gi;
const MERMAID_PDF_PLACEHOLDER =
  '<p><em>[Diagram not rendered in PDF export — view the flowchart in the document editor.]</em></p>';

function stripNonPdfHtml(content) {
  return String(content || '')
    .replace(/<img\b[^>]*>/gi, '')
    .replace(/<svg[\s\S]*?<\/svg>/gi, '')
    .replace(/<figure[\s\S]*?<\/figure>/gi, '')
    .replace(/<canvas[\s\S]*?<\/canvas>/gi, '')
    .replace(MERMAID_PRE_BLOCK, MERMAID_PDF_PLACEHOLDER);
}

function renderSectionBody(s, styles, fontConfig, captionTracker, soaData, trialSchemaImageUrl, titleElement) {
  if (isSchemaSection(s.title) && trialSchemaImageUrl) {
    // No fixed height — a forced 380pt image was orphaning the 1.2 title and
    // leaving blank pages when the image did not fit the remaining space.
    return (
      <Image
        src={trialSchemaImageUrl}
        style={{
          width: PDF_PORTRAIT_USABLE_WIDTH_PT,
          maxHeight: 300,
          objectFit: 'contain',
        }}
      />
    );
  }
  if (isSoaSection(s.title) && hasUsableSoaData(soaData)) {
    return (
      <SoaPdfTable
        soaData={soaData}
        fontConfig={fontConfig}
        caption={captionTracker.next(s.title, extractPdfTableDescription(s.title))}
        sectionTitle={titleElement}
      />
    );
  }
  const prepared = stripNonPdfHtml(prepareSectionContentForExport(s.output, s.title, s.mediaNumbering));
  const blocks = trimLeadingEmptyBlocks(
    stripLeadingDuplicateHeadingBlocks(
      richTextToBlocks(prepared, {
        sectionRootPrefix: (s.title.match(/^(\d+(?:\.\d+)*)/) || [])[1] || null,
      }),
      s.title,
    ),
  );
  return renderBlocks(blocks, styles, fontConfig, captionTracker, s.title, titleElement);
}

function renderSectionBlock(spec, styles, fontConfig, captionTracker, soaData, trialSchemaImageUrl, breakBefore) {
  const s = spec.section;
  const isSoa = isSoaSection(s.title) && hasUsableSoaData(soaData);
  const isSchema = isSchemaSection(s.title) && trialSchemaImageUrl;
  const displayTitle = isSoa ? normalizeScheduleOfActivitiesHeading(s.title) : s.title;
  return (
    <View
      key={s._id || s.title}
      break={isSoa ? false : breakBefore}
      wrap
      style={styles.sectionBlock}
    >
      {/* Schema: allow wrap so a tall image never gets Yoga-squashed */}
      {isSchema ? (
        <View wrap>
          <Text style={[styles.sectionTitle, { marginBottom: 4 }]} minPresenceAhead={40}>
            {flattenHybridTextNodes(renderHybridPdfText(displayTitle, styles.sectionTitle, `sec-${s._id || s.title}-`))}
          </Text>
          {renderSectionBody(s, styles, fontConfig, captionTracker, soaData, trialSchemaImageUrl)}
        </View>
      ) : isSoa ? (
        // SOA: the title is NOT rendered here as an independent sibling. A plain flowing
        // Text has no space guarantee, so Yoga could place it near the bottom of a page
        // (it's short, it fits) while pushing the table's actual first chunk to the next
        // page — orphaning the title above a blank gap. Instead the title element is handed
        // to SoaPdfTable, which renders it INSIDE the same atomic (wrap={false}) block as
        // the caption and the table's first chunk, guaranteeing they move together.
        renderSectionBody(
          s,
          styles,
          fontConfig,
          captionTracker,
          soaData,
          trialSchemaImageUrl,
          <Text style={[styles.sectionTitle, { marginBottom: 4 }]}>
            {flattenHybridTextNodes(renderHybridPdfText(displayTitle, styles.sectionTitle, `sec-${s._id || s.title}-`))}
          </Text>,
        )
      ) : (
        // General sections: title is NOT rendered here as an independent sibling either —
        // renderSectionBody/renderBlocks decides whether it can safely glue the title into a
        // leading table's first chunk (same reasoning as the SOA branch above) or must render
        // it as an ordinary leading element (when real content already follows it).
        renderSectionBody(
          s,
          styles,
          fontConfig,
          captionTracker,
          soaData,
          trialSchemaImageUrl,
          <Text key="title" style={styles.sectionTitle}>
            {flattenHybridTextNodes(renderHybridPdfText(displayTitle, styles.sectionTitle, `sec-${s._id || s.title}-`))}
          </Text>,
        )
      )}
    </View>
  );
}

/**
 * Emit pages in protocol order.
 * Portrait content is split into one Page per top-level section (1, 2, 3…) so
 * react-pdf does not re-resolve lineHeight across a 50+ page continuous Page
 * (which crashes fixed footers — diegomura/react-pdf#3452).
 */
function buildOrderedPageNodes({
  pageSpecs,
  styles,
  fontConfig,
  captionTracker,
  soaData,
  trialSchemaImageUrl,
  headerFooterInfo,
  hfSize,
}) {
  const nodes = [];
  let portraitRun = [];
  let portraitRunKey = 0;

  const flushPortraitRun = () => {
    if (!portraitRun.length) return;
    const specs = portraitRun;
    portraitRun = [];

    // Split this portrait run into pages by main section number.
    const groups = [];
    let currentGroup = [];
    let currentMain = null;
    specs.forEach((spec) => {
      const main = mainSectionNumber(spec.section?.title) || '_';
      if (currentGroup.length && main !== currentMain) {
        groups.push(currentGroup);
        currentGroup = [];
      }
      currentMain = main;
      currentGroup.push(spec);
    });
    if (currentGroup.length) groups.push(currentGroup);

    groups.forEach((group) => {
      const runId = portraitRunKey;
      portraitRunKey += 1;
      nodes.push(
        <Page key={`protocol-portrait-${runId}`} size="A4" wrap style={styles.page}>
          <ProtocolPdfHeaderFooter
            info={headerFooterInfo || EMPTY_PROTOCOL_HEADER_FOOTER}
            fontSize={hfSize}
            horizontalMargin={PORTRAIT_MARGIN_H}
            showHeader
            showFooter
          />
          {group.map((spec) =>
            renderSectionBlock(
              spec,
              styles,
              fontConfig,
              captionTracker,
              soaData,
              trialSchemaImageUrl,
              false,
            ),
          )}
        </Page>,
      );
    });
  };

  pageSpecs.forEach((spec, pageIdx) => {
    if (spec.orientation === 'landscape') {
      flushPortraitRun();
      nodes.push(
        <Page
          key={`section-landscape-${spec.section._id || spec.section.title || pageIdx}`}
          size="A4"
          orientation="landscape"
          wrap
          style={styles.landscapePage}
        >
          <ProtocolPdfHeaderFooter
            info={headerFooterInfo || EMPTY_PROTOCOL_HEADER_FOOTER}
            fontSize={hfSize}
            horizontalMargin={LANDSCAPE_MARGIN_H}
            showHeader
            showFooter
          />
          {renderSectionBlock(spec, styles, fontConfig, captionTracker, soaData, trialSchemaImageUrl, false)}
        </Page>,
      );
      return;
    }
    portraitRun.push(spec);
  });
  flushPortraitRun();
  return nodes;
}

const ProtocolPdfDocument = ({
  sections,
  fontConfig,
  soaData,
  trialSchemaImageUrl,
  headerFooterInfo = EMPTY_PROTOCOL_HEADER_FOOTER,
  titlePageConfig = null,
}) => {
  const styles = buildPdfStyles(fontConfig);
  const hfSize = sanitizePdfPt(fontConfig?.headerFooter?.size, 10);
  const showTitlePage = Boolean(titlePageConfig?.enabled);
  const captionTracker = createTableCaptionTracker();
  const pageSpecs = sectionsToPageSpecs(sections, soaData, trialSchemaImageUrl);

  const titlePage = showTitlePage ? (
    <Page
      key="title-page"
      size="A4"
      style={[styles.page, { paddingTop: 64, paddingBottom: 40 }]}
    >
      <ProtocolPdfHeaderFooter
        info={headerFooterInfo || EMPTY_PROTOCOL_HEADER_FOOTER}
        fontSize={hfSize}
        horizontalMargin={PORTRAIT_MARGIN_H}
        showHeader
        showFooter={false}
      />
      <ProtocolPdfTitlePage config={titlePageConfig} />
    </Page>
  ) : null;

  const bodyPages = buildOrderedPageNodes({
    pageSpecs,
    styles,
    fontConfig,
    captionTracker,
    soaData,
    trialSchemaImageUrl,
    headerFooterInfo,
    hfSize,
  });

  return (
    <Document>
      {titlePage}
      {bodyPages}
    </Document>
  );
};

export default ProtocolPdfDocument;
